import { useContext } from 'react';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { FormFieldPresets } from '@domain/form-field-presets';
import { useClubCountryIso2 } from '@hooks/use-club-country-iso2/use-club-country-iso2';
import { useGenericQuery } from '@hooks/use-generic-query';
import { request } from '@utils/request/request';
import { RequestError, RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants';

const getFormFieldsPresets = async (countryIso?: string) => {
  const base = `${import.meta.env.VITE_OFFERS_HOST}/v1/nightgraph/form-field-presets`;
  const url = countryIso ? `${base}?countryIso=${encodeURIComponent(countryIso)}` : base;

  return request({ url, method: RequestMethod.GET });
};

/**
 * Fetches form field presets for the current club.
 *
 * Reads the club's country from `CompanyClubContext` (alpha-3), maps it to alpha-2, and
 * forwards it as `?countryIso=XX` so the backend can return country-specific presets
 * (e.g. Portuguese NIF / `tax_id_pt`).
 *
 * The country param is only sent when the Portuguese NIF feature is active — either via
 * the global `isPortugueseNifEnabled` flag or the per-company `isPortugueseNifEnabledForCompany`
 * whitelist. While neither is on the hook behaves exactly as before (no query param).
 */
export const useGetFormFieldsPresets = () => {
  const { isPortugueseNifEnabled } = useContext(FeatureFlagContext);
  const { isPortugueseNifEnabledForCompany } = useContext(CompanyClubContext);
  const isPortugueseNifActive = isPortugueseNifEnabled || isPortugueseNifEnabledForCompany;
  const countryIso = useClubCountryIso2();
  const effectiveCountryIso = isPortugueseNifActive ? countryIso : undefined;

  return useGenericQuery<FormFieldPresets, RequestError>(
    [QUERY_KEYS.getFormFields, effectiveCountryIso],
    () => getFormFieldsPresets(effectiveCountryIso),
    {
      staleTime: Infinity,
      gcTime: Infinity,
    }
  );
};
